import React, { useContext, useEffect, useState } from 'react'
import { Grid, TextField, Typography, styled } from '@mui/material'
import { Clipboard, DialogPopup } from '../components'
import { translate } from '../i18n'
import {
    PREVIEW_URL,
    PRODUCTION_URL,
    ProjectPreviewPopupProps,
} from './ProjectPreviewPopup.types'
import { getProjectRedirect, updateProjectRedirect } from '../api/Projects'
import { ProjectRedirect } from '../model/Project'
import { FeedbackContext } from '../feedback/FeedbackContext'

const Image = styled('img')(({ theme }) => ({
    width: '100%',
}))

function ProjectPreviewPopup({
    stage,
    project,
    onClose,
}: ProjectPreviewPopupProps) {
    const feedbackContext = useContext(FeedbackContext)
    const [url] = useState(() => {
        if (stage === 'dev') {
            return `${PREVIEW_URL}/${project.id}`
        }
        return `${PRODUCTION_URL}/${project.id}`
    })
    const [redirect, setRedirect] = useState<ProjectRedirect | null>(null)
    const [editDestination, setEditDestination] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (stage === 'prod') {
            getProjectRedirect(project.id)
                .then((r) => {
                    setRedirect(r)
                    setEditDestination(r.destinationUrl)
                })
                .catch(() => {})
        }
    }, [stage, project.id])

    const onSaveDestination = () => {
        if (!redirect || editDestination === redirect.destinationUrl) return
        setSaving(true)
        updateProjectRedirect(project.id, {
            destinationUrl: editDestination,
        })
            .then((r) => {
                setRedirect(r)
                setEditDestination(r.destinationUrl)
                feedbackContext.showSuccess('Redirect destination updated')
            })
            .catch((err: any) =>
                feedbackContext.showError(err.message || 'Failed to update')
            )
            .finally(() => setSaving(false))
    }

    return (
        <DialogPopup
            open
            title={translate(`projects.${stage}.title`) as string}
            maxWidth="xs"
            fullWidth
            onClose={onClose}
            button={{
                text: translate('buttons.open'),
                href: url,
            }}
            leftButton={
                stage === 'prod' && project.qr_url
                    ? {
                          text: translate('buttons.download_qr') as string,
                          href: project.qr_url,
                      }
                    : undefined
            }
        >
            <Typography variant="body2" paragraph>
                {translate(`projects.${stage}.text`, project)}
            </Typography>
            <Grid
                container
                justifyContent="center"
                alignContent="center"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
            >
                {stage === 'prod' && (
                    <Grid item xs={12} md={10}>
                        <Image src={project.qr_url} alt="qr" />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Clipboard
                        value={
                            stage === 'prod' && redirect
                                ? redirect.redirectUrl
                                : url
                        }
                    />
                </Grid>

                {stage === 'prod' && redirect && (
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Destination URL"
                            value={editDestination}
                            onChange={(e) =>
                                setEditDestination(e.target.value)
                            }
                            size="small"
                            disabled={saving}
                            onBlur={onSaveDestination}
                            helperText="Edit and blur to save"
                        />
                    </Grid>
                )}
            </Grid>
        </DialogPopup>
    )
}

export default ProjectPreviewPopup
